import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Question, QuestionGroup } from '@/types/database';

export function useAssessmentSimulation(jobPositionId?: string) {
  const groupsQuery = useQuery({
    queryKey: ['simulation-groups', jobPositionId],
    queryFn: async () => {
      if (!jobPositionId) return [] as QuestionGroup[];
      const { data, error } = await supabase
        .from('question_groups')
        .select('*')
        .contains('job_position_ids', [jobPositionId])
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data || []) as QuestionGroup[];
    },
    enabled: !!jobPositionId,
  });

  const groupIds = groupsQuery.data?.map((g) => g.id) || [];

  const questionsQuery = useQuery({
    queryKey: ['simulation-questions', groupIds.join(',')],
    queryFn: async () => {
      if (groupIds.length === 0) return [] as Question[];
      const { data, error } = await supabase
        .from('questions')
        .select('*')
        .in('group_id', groupIds)
        .eq('is_enabled', true)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data || []) as Question[];
    },
    enabled: groupIds.length > 0,
  });

  const questionsByGroup = useMemo(() => {
    const map: Record<string, Question[]> = {};
    const groups = groupsQuery.data || [];
    const questions = questionsQuery.data || [];

    const pickRandom = (arr: Question[], count: number) => {
      const shuffled = [...arr];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled.slice(0, count);
    };

    groups.forEach((group) => {
      const qs = questions.filter((q) => q.group_id === group.id);
      const must = qs.filter((q) => q.is_must_have);
      const optional = qs.filter((q) => !q.is_must_have);
      const mustNeed = Math.min(group.must_have_questions, must.length);
      const optNeed = Math.min(
        Math.max(group.questions_per_test - mustNeed, 0),
        optional.length
      );
      const selected = [
        ...pickRandom(must, mustNeed),
        ...pickRandom(optional, optNeed),
      ];
      map[group.id] = selected;
    });
    return map;
  }, [groupsQuery.data, questionsQuery.data]);

  return {
    questionGroups: groupsQuery.data || [],
    questionsByGroup,
    isLoading: groupsQuery.isLoading || questionsQuery.isLoading,
    error: groupsQuery.error || questionsQuery.error,
  };
}
